<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { NButton, NEmpty, NInput, NSpin, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import PromptLibraryRow from './PromptLibraryRow.vue'
import PromptEditor from './PromptEditor.vue'
import PromptAddForm from './PromptAddForm.vue'
import { promptFactoryButtonProps } from './PromptFactoryButton'
import { useAgentPromptsStore, type AgentPrompt } from '../stores/agent_prompts'
import { useAgentReposStore } from '../stores/agent_repos'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin page to curate canned prompts for the /agent composer's new-session
// picker — either global (shown for every repo) or scoped to one
// repo by slug. Separate from AdminAgentRepos.vue per the parent PRD: a
// repo can accumulate several prompts, which would clutter the repo form.
//
// Master–detail layout: the ordered, searchable list (PromptLibraryRow)
// on the left, the editor for the selected prompt (PromptEditor) or the
// add form (PromptAddForm) on the right; the two panes stack on phones.
const promptsStore = useAgentPromptsStore()
const reposStore = useAgentReposStore()
const message = useMessage()
useDocumentTitle('Prompt library')

interface PromptGroup {
  key: string
  label: string
  prompts: AgentPrompt[]
}

// naive-ui's NSelect option `value` is typed `string | number | undefined` —
// it has no `null` member, so "Global" can't be an option with `value: null`.
// `''` is the sentinel or empty repo_slug; converted to/from `null` only at
// the store-call boundary (a slug can never be empty, so no collision).
const GLOBAL_SCOPE = ''

const scopeOptions = computed(() => [
  { label: 'General (all repos)', value: GLOBAL_SCOPE },
  ...reposStore.repos.map((r) => ({ label: r.slug, value: r.slug })),
])

// Grouped for display — "General" first, then one section per repo that has
// at least one prompt, sorted by position within each group.
const groups = computed<PromptGroup[]>(() => {
  const bySlug = new Map<string | null, AgentPrompt[]>()
  for (const prompt of promptsStore.prompts) {
    const list = bySlug.get(prompt.repo_slug) ?? []
    list.push(prompt)
    bySlug.set(prompt.repo_slug, list)
  }
  const sortByPosition = (list: AgentPrompt[]) => list.toSorted((a, b) => a.position - b.position)

  const result: PromptGroup[] = []
  const global = bySlug.get(null)
  if (global) result.push({ key: 'global', label: 'General', prompts: sortByPosition(global) })
  for (const [slug, list] of bySlug) {
    if (slug === null) continue
    result.push({ key: slug, label: slug, prompts: sortByPosition(list) })
  }
  return result
})

// Client-side filter — with a dozen+ prompts across several repos,
// scrolling through every card to find one by eye doesn't scale. Matches
// against label or text, case-insensitive; groups with no matches are
// hidden entirely rather than shown empty.
const searchQuery = ref('')
const visibleGroups = computed<PromptGroup[]>(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return groups.value
  return groups.value
    .map((group) => ({
      ...group,
      prompts: group.prompts.filter(
        (p) => p.label.toLowerCase().includes(query) || p.text.toLowerCase().includes(query),
      ),
    }))
    .filter((group) => group.prompts.length > 0)
})
const noSearchMatches = computed(() => searchQuery.value.trim().length > 0 && visibleGroups.value.length === 0)

// The add form replaces the editor in the right pane while open; the
// header toggle opens and closes it.
const addFormOpen = ref(false)

// The prompt shown in the editor. Defaults to the first visible prompt so
// the editor is never an empty pane, and falls back the same way when the
// selected prompt is deleted.
const selectedId = ref<number | null>(null)
const selectedPrompt = computed<AgentPrompt | null>(() => {
  const chosen = promptsStore.prompts.find((p) => p.id === selectedId.value)
  return chosen ?? visibleGroups.value[0]?.prompts[0] ?? null
})

// On phones the editor sits below the list, so bring it into view after
// picking a prompt rather than leaving the selection invisible.
const NARROW_QUERY = '(max-width: 767px)'
const editorPane = ref<HTMLElement | null>(null)

async function selectPrompt(id: number): Promise<void> {
  selectedId.value = id
  addFormOpen.value = false
  await nextTick()
  if (typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches) {
    editorPane.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }
}

onMounted(async () => {
  try {
    await Promise.all([promptsStore.fetchAll(), reposStore.fetchAll()])
  } catch {
    /* loadError surfaces in template */
  }
})

function findGroup(id: number): PromptGroup | undefined {
  return groups.value.find((g) => g.prompts.some((p) => p.id === id))
}

async function handleUpdate(id: number, params: { label?: string; text?: string }): Promise<void> {
  try {
    await promptsStore.update(id, params)
    message.success('Prompt updated')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to update prompt')
  }
}

async function handleScopeChange(id: number, repoSlug: string | null): Promise<void> {
  try {
    await promptsStore.update(id, { repo_slug: repoSlug })
    message.success('Scope updated')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to update scope')
  }
}

async function handleDestroy(id: number): Promise<void> {
  try {
    await promptsStore.destroy(id)
    message.success('Prompt removed')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to remove prompt')
  }
}

async function move(id: number, delta: 1 | -1): Promise<void> {
  const group = findGroup(id)
  if (!group) return
  const ids = group.prompts.map((p) => p.id)
  const index = ids.indexOf(id)
  const swapWith = index + delta
  if (swapWith < 0 || swapWith >= ids.length) return
  ;[ids[index], ids[swapWith]] = [ids[swapWith], ids[index]]
  try {
    await promptsStore.reorder(ids)
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to reorder prompts')
  }
}
</script>

<template>
  <RouteShell>
    <section class="mx-auto flex max-w-6xl flex-col gap-5 pb-6">
      <header class="flex flex-wrap items-start gap-3">
        <div class="flex min-w-60 flex-1 flex-col gap-1.5">
          <h1 class="page-title">Prompt library</h1>
          <p class="m-0 text-sm text-ink-muted">
            Saved prompts appear when you type
            <kbd class="rounded border border-border-subtle px-1.5 font-mono text-xs text-ink">/</kbd> in a session
            composer, in this order. A prompt is either General (shown for every repo) or scoped to one repo.
          </p>
        </div>
        <NButton
          v-bind="promptFactoryButtonProps"
          class="min-h-11 shrink-0 px-4 font-semibold sm:min-h-10"
          :aria-expanded="addFormOpen"
          data-testid="prompt-add-toggle"
          @click="addFormOpen = !addFormOpen"
          >{{ addFormOpen ? 'Close' : '+ Add a prompt' }}</NButton
        >
      </header>

      <div v-if="promptsStore.loading && !promptsStore.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>

      <div
        v-else-if="promptsStore.loadError"
        role="alert"
        class="rounded-xl border border-status-alert/40 bg-status-alert/10 px-4 py-3 text-sm text-ink-reading"
        data-testid="prompt-library-load-error"
      >
        Failed to load prompts: {{ promptsStore.loadError }}
      </div>

      <div v-else class="flex flex-wrap items-start gap-5">
        <section aria-label="Prompts" class="flex min-w-0 flex-[1_1_320px] flex-col gap-3 md:max-w-[460px]">
          <NEmpty v-if="promptsStore.prompts.length === 0" description="No prompts configured yet." class="my-12" />

          <template v-else>
            <NInput
              v-model:value="searchQuery"
              placeholder="Search prompts by label or text…"
              clearable
              :input-props="{ 'aria-label': 'Search prompts' }"
              data-testid="prompt-search"
            />

            <NEmpty
              v-if="noSearchMatches"
              description="No prompts match your search."
              class="my-8"
              data-testid="prompt-search-empty"
            />

            <div
              v-else
              class="overflow-hidden rounded-2xl border border-border-subtle bg-surface-raised"
              data-testid="prompt-library-groups"
            >
              <section
                v-for="group in visibleGroups"
                :key="group.key"
                :aria-labelledby="`prompt-group-heading-${group.key}`"
                :data-testid="`prompt-group-${group.key}`"
              >
                <h2
                  :id="`prompt-group-heading-${group.key}`"
                  class="m-0 flex items-center gap-2 px-4 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint sm:px-5"
                >
                  <span :class="group.key === 'global' ? '' : 'font-mono normal-case tracking-normal'">{{
                    group.label
                  }}</span>
                  <span class="font-normal">({{ group.prompts.length }})</span>
                </h2>
                <ol class="m-0 list-none p-0">
                  <PromptLibraryRow
                    v-for="(prompt, index) in group.prompts"
                    :key="prompt.id"
                    :prompt="prompt"
                    :is-first="index === 0"
                    :is-last="index === group.prompts.length - 1"
                    :selected="!addFormOpen && selectedPrompt?.id === prompt.id"
                    @select="selectPrompt"
                    @move-up="move($event, -1)"
                    @move-down="move($event, 1)"
                  />
                </ol>
              </section>
            </div>
          </template>
        </section>

        <div ref="editorPane" class="min-w-0 flex-[999_1_380px] scroll-mt-4">
          <PromptAddForm v-if="addFormOpen" :scope-options="scopeOptions" @close="addFormOpen = false" />
          <PromptEditor
            v-else-if="selectedPrompt"
            :key="selectedPrompt.id"
            :prompt="selectedPrompt"
            :scope-options="scopeOptions"
            :on-update="handleUpdate"
            @scope-change="handleScopeChange"
            @destroy="handleDestroy"
          />
        </div>
      </div>
    </section>
  </RouteShell>
</template>
